/** @format */

import axios, { AxiosRequestConfig } from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";
import {
    ICollectSubscription,
    ICollectSubscriptionResult,
    ICollectSubscriptionWithAccess,
} from "../../utility/types/collectTypes";

// ─────────────────────────────────────────────────────────────────────────
// Collect billing API surface (Story 2).
//
// Talks to the Story 1 endpoints mounted at the API root:
//   GET    /collect/subscription
//   POST   /collect/subscription
//   POST   /collect/subscription/resubscribe
//   POST   /collect/subscription/cancel
//   POST   /collect/subscription/reactivate
//   PUT    /collect/subscription/payment-method
//
// Every request carries the tenant via the `x-business-id` header; the
// Cognito business Bearer token is injected automatically by the global
// axios interceptor for `/collect/` URLs.
// ─────────────────────────────────────────────────────────────────────────

function withBusiness(
    businessId: string,
    extra: AxiosRequestConfig = {},
): AxiosRequestConfig {
    return {
        ...extra,
        headers: {
            ...(extra.headers || {}),
            "x-business-id": businessId,
        },
    };
}

function pickError(error: any, fallback: string): string {
    return error?.response?.data?.message || error?.message || fallback;
}

/** GET the business's Collect subscription plus its access summary (Story 5). */
export const submitGetCollectSubscription = async (
    businessId: string,
): Promise<ICollectSubscriptionWithAccess> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/subscription`,
            withBusiness(businessId),
        );
        return {
            subscription:
                (res.data?.data as ICollectSubscription | null) ?? null,
            access: res.data?.access ?? null,
        };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load subscription."));
    }
};

/** Create the subscription (intro→standard). Returns SCA hint when needed. */
export const submitCreateCollectSubscription = async (
    businessId: string,
    paymentMethodId: string,
): Promise<ICollectSubscriptionResult> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/subscription`,
            { paymentMethodId },
            withBusiness(businessId),
        );
        return {
            subscription: res.data?.data as ICollectSubscription,
            requiresAction: !!res.data?.requiresAction,
            clientSecret: res.data?.clientSecret,
        };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to start subscription."));
    }
};

/**
 * One-click resubscribe. `paymentMethodId` is optional — omitted, the backend
 * reuses the retained default payment method; required after the 60-day purge.
 */
export const submitResubscribeCollectSubscription = async (
    businessId: string,
    paymentMethodId?: string,
): Promise<ICollectSubscriptionResult> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/subscription/resubscribe`,
            paymentMethodId ? { paymentMethodId } : {},
            withBusiness(businessId),
        );
        return {
            subscription: res.data?.data as ICollectSubscription,
            requiresAction: !!res.data?.requiresAction,
            clientSecret: res.data?.clientSecret,
        };
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to resubscribe."));
    }
};

/** Cancel — defaults to end-of-period; `immediate` terminates now. */
export const submitCancelCollectSubscription = async (
    businessId: string,
    options: { reason?: string; immediate?: boolean } = {},
): Promise<ICollectSubscription> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/subscription/cancel`,
            { reason: options.reason, immediate: !!options.immediate },
            withBusiness(businessId),
        );
        return res.data?.data as ICollectSubscription;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to cancel subscription."));
    }
};

/** Reverse a pending cancellation before the period ends. */
export const submitReactivateCollectSubscription = async (
    businessId: string,
): Promise<ICollectSubscription> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/subscription/reactivate`,
            {},
            withBusiness(businessId),
        );
        return res.data?.data as ICollectSubscription;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to reactivate subscription."));
    }
};

/** Update the default payment method used for future charges. */
export const submitUpdateCollectPaymentMethod = async (
    businessId: string,
    paymentMethodId: string,
): Promise<ICollectSubscription> => {
    try {
        const res = await axios.put(
            `${API_BASE_URL}/collect/subscription/payment-method`,
            { paymentMethodId },
            withBusiness(businessId),
        );
        return res.data?.data as ICollectSubscription;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to update payment method."));
    }
};
